/**
 * ShoppingList Component
 *
 * Displays the shopping list with items grouped by category.
 * Allows users to check off items as they shop and supports offline access.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShoppingListStore } from '../../stores/shoppingListStore';
import { useMealPlanStore } from '../../stores/mealPlanStore';
import { CategorySection } from './CategorySection';
import './shopping.css';

export function ShoppingList() {
  const { t } = useTranslation(['shopping', 'common']);
  const {
    currentShoppingList,
    isLoading,
    error,
    isOnline,
    getItemsByCategory,
    toggleItemChecked,
    generateShoppingList,
  } = useShoppingListStore();

  const { currentMealPlan } = useMealPlanStore();

  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const categoryGroups = getItemsByCategory();

  useEffect(() => {
    // Component initialization
    // Shopping list is already loaded from store persistence
  }, []);

  // Handler for toggling item checked state
  const handleToggleItem = async (itemId: number) => {
    if (!currentShoppingList) return;

    try {
      await toggleItemChecked(currentShoppingList.id, itemId);
    } catch (err) {
      // Error is handled by store
    }
  };

  // Handler for generating shopping list from meal plan
  const handleGenerateShoppingList = async () => {
    if (!currentMealPlan) {
      setGenerateError(t('noMealPlan'));
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);

    try {
      await generateShoppingList({ meal_plan_id: currentMealPlan.id });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate shopping list';
      setGenerateError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  if (error) {
    return (
      <div className="shopping-list-container">
        <div className="error-message" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-list-container">
      <div className="shopping-list-header">
        <div className="shopping-list-header-row">
          <h1 style={{ margin: 0 }}>{t('shoppingList')}</h1>
          {currentMealPlan && (
            <button
              onClick={handleGenerateShoppingList}
              disabled={isGenerating || isLoading}
              className="btn-primary btn-generate"
            >
              {isGenerating ? t('generating') : t('generateFromMealPlan')}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p className="shopping-list-subtitle">
            {t('checkOffItems')}
          </p>
          {!isOnline && (
            <div
              className="offline-indicator"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.25rem 0.75rem',
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '0.25rem',
                fontSize: '0.875rem',
                color: '#92400e',
                fontWeight: 500,
              }}
              role="status"
              aria-live="polite"
            >
              <span style={{ marginRight: '0.5rem' }}>⚠️</span>
              {t('common:offlineMode')}
            </div>
          )}
        </div>
      </div>

      {generateError && (
        <div className="error-message" role="alert">
          {generateError}
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">
          <p>{t('loadingShoppingList')}</p>
        </div>
      ) : !currentShoppingList ? (
        <div className="empty-state">
          <p>{t('noShoppingList')}</p>
          <p className="empty-state-hint">
            {t('generateHint')}
          </p>
          {currentMealPlan ? (
            <button
              onClick={handleGenerateShoppingList}
              disabled={isGenerating}
              className="btn-primary"
            >
              {isGenerating ? t('generating') : t('generateFromMealPlan')}
            </button>
          ) : (
            <a href="/meal-plan" className="btn-primary">
              {t('common:goToMealPlan')}
            </a>
          )}
        </div>
      ) : categoryGroups.length === 0 ? (
        <div className="empty-state">
          <p>{t('emptyList')}</p>
          <p className="empty-state-hint">
            {t('emptyListHint')}
          </p>
        </div>
      ) : (
        <div className="shopping-list-content">
          <div className="shopping-list-info">
            <p className="item-count">
              {t('items', { count: currentShoppingList.shopping_items?.length || 0 })}
            </p>
          </div>

          <div className="category-groups">
            {categoryGroups.map((group) => (
              <CategorySection
                key={group.category}
                categoryGroup={group}
                onToggleItem={handleToggleItem}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ShoppingList;
